import { useState, useEffect } from "react";
import { Modal } from "antd";
import { IconCloseSvg } from "@/components/svgs";
import { DATETIME_FORMAT_STANDARD, NOTIFICATION_TYPE } from "@/config/constants";
import { initFormAddAlertEmptyLegs } from "@/utils/initializing";
import { toast } from "@/modules/toast";
import { ModalStatus } from "../ModalStatus";
import { FormAddAlert } from "./FormAddAlert";
import { FormUpdateAlert } from "./FormUpdateAlert";
import { FormWarning } from "./FormWarning";
import { setEmptyLegsAlert, updateEmptyLegsAlert } from "@/stores/apis/apiSettings";
import { useSelector } from "react-redux";
import { selectAuthState } from "@/stores/slices/authSlice";
import dayjs from "dayjs";
import moment from "moment-timezone";

const DUPLICATION_RANGE = 40;
const ALERT_TYPE = {
	route: "ROUTE",
	airport: "AIRPORT",
};

const AIRPORT_TYPE = {
	departure: "DEPARTURE",
	arrival: "ARRIVAL",
};

export function ModalAddAlertEmptyLegs({
	visible = false,
	onClose,
	type = "add",
	onAddSuccess,
	model,
	listRoutes = [],
}) {
	const { user } = useSelector(selectAuthState);

	const [loading, setLoading] = useState(false);
	const [formAlert, setFormAlert] = useState(initFormAddAlertEmptyLegs);
	const [showStatus, setShowStatus] = useState(false);
	const [showWarning, setShowWarning] = useState(false);

	useEffect(() => {
		if (visible && type === "edit" && model) {
			let data = {
				from_airport: model?.from_airport,
				to_airport: model?.to_airport,
				type_airport: [], // DEPARTURE - ARRIVAL
				airport: model?.from_airport ?? model?.to_airport,
				type: model?.type, // ROUTE - AIRPORT
				from_datetime: null,
				to_datetime: null,
			};
			let departTime = model?.users?.find((v) => v?.user?._id === user?._id);
			if (departTime && departTime.from_datetime && departTime.to_datetime) {
				const timezone = data?.from_airport?.location?.timezone || data?.to_airport?.location?.timezone;
				data.from_datetime = dayjs(
					moment(departTime?.from_datetime).tz(timezone).format(DATETIME_FORMAT_STANDARD),
				).toISOString();
				data.to_datetime = dayjs(
					moment(departTime?.to_datetime).tz(timezone).format(DATETIME_FORMAT_STANDARD),
				).toISOString();
			}
			if (model?.type === ALERT_TYPE.airport) {
				let typeAirport = [];
				if (model?.from_airport) typeAirport.push(AIRPORT_TYPE.departure);
				if (model?.to_airport) typeAirport.push(AIRPORT_TYPE.arrival);
				data.type_airport = typeAirport;
			}
			setFormAlert({ ...data });
		}
	}, [visible, type, model, user]);

	const onSubmitEmptyLegsAlert = async () => {
		let payload = {
			type: formAlert.type,
			from_datetime: formAlert.from_datetime,
			to_datetime: formAlert.to_datetime,
		};

		if (formAlert.type === ALERT_TYPE.route) {
			payload = {
				...payload,
				from_airport: formAlert?.from_airport?._id,
				to_airport: formAlert?.to_airport?._id,
			};
		} else if (formAlert.type === ALERT_TYPE.airport) {
			if (formAlert.type_airport.includes(AIRPORT_TYPE.departure)) {
				payload.from_airport = formAlert?.airport?._id;
			}
			if (formAlert.type_airport.includes(AIRPORT_TYPE.arrival)) {
				payload.to_airport = formAlert?.airport?._id;
			}
		}

		// format time range with timezone
		let timezone =
			formAlert.type === ALERT_TYPE.airport
				? formAlert?.airport?.location?.timezone
				: formAlert?.from_airport?.location?.timezone;
		if (payload.from_datetime && timezone) {
			let value = dayjs(payload.from_datetime).format(DATETIME_FORMAT_STANDARD);
			payload.from_datetime = dayjs.tz(value, timezone).toISOString();
		}
		if (payload.to_datetime && timezone) {
			let value = dayjs(payload.to_datetime).format(DATETIME_FORMAT_STANDARD);
			payload.to_datetime = dayjs.tz(value, timezone).toISOString();
		}

		if (loading) return;
		setLoading(true);

		let result = await setEmptyLegsAlert(payload);
		if (result && result.success) {
			setShowStatus(true);
			setFormAlert(initFormAddAlertEmptyLegs);
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Add Alert Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const onSubmitEditAlert = async () => {
		let payload = {
			from_datetime: formAlert.from_datetime,
			to_datetime: formAlert.to_datetime,
		};

		if (formAlert.type === ALERT_TYPE.airport) {
			if (formAlert.type_airport.includes(AIRPORT_TYPE.departure)) {
				payload.from_airport = formAlert?.airport?._id;
			} else {
				payload.from_airport = null;
			}
			if (formAlert.type_airport.includes(AIRPORT_TYPE.arrival)) {
				payload.to_airport = formAlert?.airport?._id;
			} else {
				payload.to_airport = null;
			}
		}

		// format time range with timezone
		let timezone =
			formAlert.type === ALERT_TYPE.airport
				? formAlert?.airport?.location?.timezone
				: formAlert?.from_airport?.location?.timezone;
		if (payload.from_datetime && timezone) {
			let value = dayjs(payload.from_datetime).format(DATETIME_FORMAT_STANDARD);
			payload.from_datetime = dayjs.tz(value, timezone).toISOString();
		}
		if (payload.to_datetime && timezone) {
			let value = dayjs(payload.to_datetime).format(DATETIME_FORMAT_STANDARD);
			payload.to_datetime = dayjs.tz(value, timezone).toISOString();
		}

		if (loading) return;
		setLoading(true);

		let result = await updateEmptyLegsAlert(model._id, payload);
		if (result && result.success) {
			setShowStatus(true);
		} else {
			toast({
				type: NOTIFICATION_TYPE.ERROR,
				description: "Update Alert Failed.",
			});
		}

		setTimeout(() => {
			setLoading(false);
		}, 200);
	};

	const _renderConentByType = () => {
		if (type === "edit") {
			return (
				<FormUpdateAlert
					data={formAlert}
					onChange={(values) => setFormAlert({ ...values })}
					onSubmitEditAlert={() => {
						if (formAlert.type === ALERT_TYPE.airport) {
							onSubmitEditAlert();
						} else {
							if (
								!checkPotentialDuplication(
									{
										from_airport: formAlert.from_airport,
										to_airport: formAlert.to_airport,
										from_datetime: formAlert.from_datetime,
										to_datetime: formAlert.to_datetime,
									},
									listRoutes.filter((route) => route._id !== model._id),
								)
							) {
								onSubmitEditAlert();
							} else {
								setShowWarning(true);
							}
						}
					}}
				/>
			);
		}

		return (
			<FormAddAlert
				data={formAlert}
				onChange={(values) => setFormAlert({ ...values })}
				onSubmitEmptyLegsAlert={() => {
					if (formAlert.type === ALERT_TYPE.airport) {
						onSubmitEmptyLegsAlert();
					} else {
						if (
							!checkPotentialDuplication(
								{
									from_airport: formAlert.from_airport,
									to_airport: formAlert.to_airport,
									from_datetime: formAlert.from_datetime,
									to_datetime: formAlert.to_datetime,
								},
								listRoutes,
							)
						) {
							onSubmitEmptyLegsAlert();
						} else {
							setShowWarning(true);
						}
					}
				}}
			/>
		);
	};

	const checkPotentialDuplication = (targetRoute, listOfRoutes = []) => {
		if (
			!targetRoute.to_airport.location.geometry.coordinates ||
			!targetRoute.from_airport.location.geometry.coordinates
		)
			return false;
		for (let checkRoute of listOfRoutes) {
			if (
				!checkRoute.from_airport.location.geometry.coordinates ||
				!checkRoute.to_airport.location.geometry.coordinates
			) {
				return false;
			} else {
				const distanceFrom = calculateHaversineDistance(
					targetRoute.from_airport.location.geometry.coordinates,
					checkRoute.from_airport.location.geometry.coordinates,
				);
				const distanceTo = calculateHaversineDistance(
					targetRoute.to_airport.location.geometry.coordinates,
					checkRoute.to_airport.location.geometry.coordinates,
				);
				if (
					distanceFrom.mi <= DUPLICATION_RANGE &&
					distanceTo.mi <= DUPLICATION_RANGE &&
					dateRangeOverlaps(
						checkRoute.users[0].from_datetime,
						checkRoute.users[0].to_datetime,
						targetRoute.from_datetime,
						targetRoute.to_datetime,
					)
				) {
					return true;
				}
			}
		}
	};

	const calculateHaversineDistance = (from_coordinates, to_coordinates) => {
		const lat1 = parseFloat(from_coordinates[1]);
		const lat2 = parseFloat(to_coordinates[1]);
		const lon1 = parseFloat(from_coordinates[0]);
		const lon2 = parseFloat(to_coordinates[0]);

		const R = 6371; // km
		const x1 = lat2 - lat1;
		const dLat = toRadians(x1);
		const x2 = lon2 - lon1;
		const dLon = toRadians(x2);
		const a =
			Math.sin(dLat / 2) * Math.sin(dLat / 2) +
			Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
		const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
		const d = R * c;

		return {
			km: parseFloat(d.toFixed(1)),
			mi: parseFloat((d / 1.60934).toFixed(1)),
			nm: parseFloat((d / 1.852).toFixed(1)),
		};
	};

	const toRadians = (degrees) => {
		return degrees * (Math.PI / 180);
	};

	function dateRangeOverlaps(aStart, aEnd, bStart, bEnd) {
		if (!aStart || !aEnd || !bStart || !bEnd) return true;
		if (aStart <= bStart && bStart <= aEnd) return true; // b starts in a
		if (aStart <= bEnd && bEnd <= aEnd) return true; // b ends in a
		if (bStart < aStart && aEnd < bEnd) return true; // a in b
		return false;
	}

	return (
		<Modal
			centered
			closeIcon={<IconCloseSvg />}
			open={visible}
			onCancel={() => {
				setFormAlert(initFormAddAlertEmptyLegs);
				setShowWarning(false);
				onClose(false);
			}}
			footer={null}
			width="640px"
		>
			{showWarning ? (
				<FormWarning
					data={formAlert}
					onContinue={() => {
						if (type === "edit") {
							onSubmitEditAlert();
						} else {
							onSubmitEmptyLegsAlert();
						}
					}}
					onReview={() => {
						setFormAlert(initFormAddAlertEmptyLegs);
						setShowWarning(false);
						onClose(false);
					}}
					onBack={() => {
						setShowWarning(false);
					}}
				/>
			) : (
				<>{_renderConentByType()}</>
			)}
			<ModalStatus
				visible={showStatus}
				status={NOTIFICATION_TYPE.SUCCESS}
				title={type === "edit" ? "Alert Updated Successfully!" : "Alert Added Successfully!"}
				note="Congratulations! Your empty leg alert is now active. Stay tuned for matching flight opportunities!"
				onClose={() => {
					onAddSuccess?.();
					setShowStatus(false);
					setShowWarning(false);
					setTimeout(() => {
						setFormAlert(initFormAddAlertEmptyLegs);
						onClose();
					}, 300);
				}}
			/>
		</Modal>
	);
}
